import { ReactElement } from 'react';

import { isEmpty } from 'lodash';
import { CHALLENGES_ENABLED, IS_PRODUCER_ENABLED } from 'src/constants/FeatureFlags';
import { EntertainerToolbarIcon } from 'src/layouts/EntertainerToolbarIcon';
import { PromoterToolbarIcon } from 'src/layouts/PromotedToolbarIcon';

import { useCurrentEntertainerQuery } from '@queries/EntertainerQueries';
import { useCurrentUser } from '@queries/UserQueries';
import { useUserSocialCirclesQuery } from '@queries/UserSocialCircleQueries';

import { useLocation } from '@router/index';

import { isEnv } from '@utilities/isEnv';

import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { Framework7Icon } from '@components/icons/Framework7Icon';
import { LineAwesomeIcon } from '@components/icons/LineAwesomeIcon';
import { TriviaIcon } from '@components/icons/TriviaIcon';
import { VennDiagram } from '@components/icons/VennDiagram';

import { useCurrentPromoterQuery } from '../queries/PromoterQueries';

const ENABLE_TRIVIA = false;

interface TabbarRoute {
  path: string;
  icon: ReactElement;
  title: string;
  isVisible: boolean;
  requiresEventsEnabled?: boolean;
}

export const deprecatedRoutes: TabbarRoute[] = [
  {
    path: '/trivia',
    icon: <TriviaIcon />,
    title: 'Trivia',
    isVisible: ENABLE_TRIVIA,
  },
  {
    isVisible: true,
    path: '/events',
    icon: <LineAwesomeIcon name="calendar" />,
    title: 'Events',
    // requiresEventsEnabled: true,
  },
  { isVisible: true, path: '/venues', icon: <LineAwesomeIcon name="beer" />, title: 'Bars' },
  {
    isVisible: true,
    path: '/profile',
    icon: <LineAwesomeIcon name="user-circle" />,
    title: 'Profile',
  },
];

export const useTabbarRoutes = (): TabbarRoute[] => {
  const { promoters } = useCurrentPromoterQuery();
  const { currentUser } = useCurrentUser();
  const { entertainer } = useCurrentEntertainerQuery();
  const { socialCircles } = useUserSocialCirclesQuery();

  const hasEntertainer = Boolean(currentUser?.entertainerId || entertainer?._id);
  const hasApprovedPromoters: boolean =
    promoters?.some((promoter) => promoter.permissions.status === 'approved') || false;

  return [
    {
      isVisible: true,
      path: '/home',
      icon: <FontAwesomeIcon name="home" />,
      title: 'Home',
    },
    ...(CHALLENGES_ENABLED && hasEntertainer
      ? [
          {
            isVisible: true,
            path: '/challenges',
            icon: <LineAwesomeIcon name="trophy" />,
            title: 'Challenges',
          },
        ]
      : []),
    {
      isVisible: false,
      path: '/manage/circles',
      icon: <VennDiagram />,
      title: 'Circles',
    },
    {
      isVisible: true,
      path: '/manage/connections',
      icon: <VennDiagram />,
      title: 'Connections',
    },
    {
      isVisible: false,
      path: '/discover',
      icon: <Framework7Icon name="sparkles" />,
      title: 'Discover',
    },
    // { path: '/community', icon: <FontAwesomeIcon name="mug-hot" />, title: 'Tea' },
    {
      isVisible: hasEntertainer,
      path: '/manage/entertainer',
      icon: <EntertainerToolbarIcon height={28} width={28} />,
      title: 'Entertainer',
    },
    // {
    //   path: '/notifications',
    //   icon: <FontAwesomeIcon name="bell" />,
    //   title: 'Notifications',
    // },
    {
      isVisible: hasApprovedPromoters && IS_PRODUCER_ENABLED,
      path: '/manage/promoter',
      icon: <PromoterToolbarIcon height={28} width={28} />,
      title: 'Producer',
    },
    {
      isVisible: true,
      path: '/profile',
      icon: <LineAwesomeIcon name="user-circle" />,
      title: 'Profile',
    },
  ];
};

export const useTabbarIsVisible = () => {
  const location = useLocation();
  const routes = useTabbarRoutes();
  return { tabbarIsVisible: routes.map(({ path }) => path).includes(location.pathname) };
};
